import { Store } from '@ngrx/store';
import {
  InterfacePost,
  OrderformConfig,
  OrderformGlobalSettings,
  OrderformMessageType,
  OrderformOrderState,
  OrderformOrderStateMessages,
  OrderformRedirectResponse,
  OrderformSelectedPlan,
  OrderformSettingsState,
  OrderFormValidationResult,
  OrderformValidationState,
} from '../ngrx/orderform.models';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, of } from 'rxjs';
import { ElementRef, EventEmitter, Injectable, NgZone } from '@angular/core';
import { OrderformPartialState } from '../ngrx/orderform.reducer';
import {
  getAffiliateName,
  getExpressCheckoutConfig,
  getMerchantName,
  getOrder,
  getOrderAmount,
  getOrderformConfig,
  getOrderformGlobalSettings,
  getOrderformMessages,
  getOrderformPaymentPlanList,
  getOrderformPending,
  getOrderformSettings,
  getOrderformState,
  getOrderformValidation,
  getOrderItems,
  getOrderPaymentPlanId,
  getOrderPaymentUrl,
  getOrderSelectedPaymentPlan,
  getIsExpressCheckoutClicked,
  getOrderformAnonymousSessionId,
  getOrderformProductList,
  getViewedExperiments,
  getOrderformIsGdprApplied,
  getOrderformHasCookieConsent,
  getOrderformGbId,
  getInitialError,
} from '../ngrx/orderform.selectors';
import * as OrderformActions from '../ngrx/orderform.actions';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { ToasterService, ToastItemConfig } from '@digistore/ds24-ui/toaster';

import { AdvancedEventEmitter } from '../misc/advanced-event-emitter';
import { OrderformLocalStorageService } from '../services/orderform-local-storage.service';

declare const window: any;

@Injectable({
  providedIn: 'root',
})
export class OrderformFascade {
  /**
   * Check JS in Editor (Not in Editor Iframe or Preview/live)
   */
  isEditor$ = new BehaviorSubject<boolean>(!!(window.Editor && window.pageData));
  public rootState$ = this._store.select(getOrderformState);
  public submitted$ = new BehaviorSubject(false);
  public globalSettings$: Observable<OrderformGlobalSettings> = this._store.select(getOrderformGlobalSettings);
  public selectedPlan$: Observable<OrderformSelectedPlan> = this._store.select(getOrderSelectedPaymentPlan);
  public config$: Observable<OrderformConfig> = this._store.select(getOrderformConfig);
  public order$: Observable<OrderformOrderState> = this._store.select(getOrder);
  public orderAmount$: Observable<number> = this._store.select(getOrderAmount);
  public settings$: Observable<OrderformSettingsState> = this._store.select(getOrderformSettings);
  public validation$: Observable<OrderformValidationState> = this._store.select(getOrderformValidation);
  public messages$: Observable<OrderformOrderStateMessages> = this._store.select(getOrderformMessages);
  public pending$: Observable<boolean> = this._store.select(getOrderformPending).pipe(map((data) => !!data));
  public requestPending$: Observable<boolean> = this.pending$.pipe(filter((pending) => !pending));
  public paymentPlanList$ = this._store.select(getOrderformPaymentPlanList);
  public paymentPlanId$ = this._store.select(getOrderPaymentPlanId);
  public paymentUrl$ = this._store.select(getOrderPaymentUrl);
  public orderItems$ = this._store.select(getOrderItems);
  public mainProductId$ = this.config$.pipe(map((config) => +config.product_id));
  public mainProductTypeId$ = this._store.select(getOrderformProductList).pipe(map((items) => items[0].type_id));
  public siteownerId$ = this.config$.pipe(map((config) => config.siteowner_id));
  public isGdprApplied$ = this._store.select(getOrderformIsGdprApplied);
  public initialError$ = this._store.select(getInitialError);
  public hasCookieConsent$ = this._store.select(getOrderformHasCookieConsent);
  public mainProductDefaultPrice$ = this._store
    .select(getOrderformProductList)
    .pipe(map((items) => items[0].total_amounts.first.gross));
  public mainProduct$ = combineLatest([this.mainProductId$, this.orderItems$]).pipe(
    map(([id, items]) => {
      return items[id];
    })
  );
  public expressCheckout$ = this._store.select(getExpressCheckoutConfig);
  public isExpressCheckoutClicked$: Observable<boolean> = this._store.select(getIsExpressCheckoutClicked);

  public paymentLegalHint$ = this._store
    .select(getOrderformGlobalSettings)
    .pipe(map((settings) => settings.payment_legal_hint));

  public paymentPlanChanges$ = this._store.select(getOrder).pipe(map((state) => state.payment_plan_changes));

  public affiliateName$ = this._store.select(getAffiliateName);
  public merchantName$ = this._store.select(getMerchantName);

  /**
   * Nach dem Absenden des Bestellformulars werden folgenden Events in dieser Reihenfolge geworfen:
   * - formValidation.emit()
   * Wenn die Validierung überall OK ist geht es weiter mit:
   * - beforeSubmit.emit()
   * Wenn hier auch ein false kommt wird das Orderform ans Backend geschickt
   * Wenn der Request zurück gekommen ist vom Backend mit einem OK, dann erfolgt:
   * - afterSubmit.emit()
   */
  public formValidation = new AdvancedEventEmitter<void, OrderFormValidationResult, ElementRef<HTMLElement>>();
  public beforeSubmit = new AdvancedEventEmitter<void, { valid: boolean }>();
  public afterSubmit = new AdvancedEventEmitter<void, any>();
  public submitFailed = new EventEmitter<void>();

  get anonymousSessionId() {
    return firstValueFrom(this._store.select(getOrderformAnonymousSessionId));
  }

  get gbId() {
    return firstValueFrom(this._store.select(getOrderformGbId));
  }

  get viewedExperiments() {
    return firstValueFrom(this._store.select(getViewedExperiments));
  }

  //Primer redirect special case
  public primerRedirectIntercepted$ = new EventEmitter<{ redirectData: OrderformRedirectResponse }>();

  private _messagesShown = [];

  constructor(
    private _store: Store<OrderformPartialState>,
    private _ngZone: NgZone,
    private _toaster: ToasterService,
    private _localStorageService: OrderformLocalStorageService
  ) {
    /**
     * Note: loadProgressFromLocalStorage() is called from app.module.ts
     * after GrowthBook feature flag check to avoid circular dependency
     */

    /**
     * Logic to display messages via toast - with no repeats
     */
    // Wait until pending is done
    // default settings for toasts
    const settings: ToastItemConfig = {
      close: true,
    };

    /**
     * Disable Messaging in Editor
     * https://digistore.atlassian.net/browse/DS-9168
     */
    if (!this.isEditor$.value) {
      this.requestPending$.subscribe((pending) => {
        // Take messages
        this.order$.pipe(take(1)).subscribe((order) => {
          this._ngZone.runOutsideAngular(() =>
            setTimeout(() => {
              let tmpShow = [];
              (order.messages?.success || /* istanbul ignore next */ []).forEach((message) => {
                /* istanbul ignore else */
                if (!this._messagesShown.includes(message)) {
                  this._toaster
                    .success(message)
                    .afterClosed()
                    .subscribe(() => this._removeMessageFromShownList('success', message));
                }
                tmpShow.push(message);
              });
              (order.messages?.infos || /* istanbul ignore next */ []).forEach((message) => {
                /* istanbul ignore else */
                if (!this._messagesShown.includes(message)) {
                  this._toaster
                    .info(message)
                    .afterClosed()
                    .subscribe(() => this._removeMessageFromShownList('infos', message));
                }
                tmpShow.push(message);
              });
              (order.messages?.warnings || /* istanbul ignore next */ []).forEach((message) => {
                /* istanbul ignore else */
                if (!this._messagesShown.includes(message)) {
                  this._toaster
                    .warn(message)
                    .afterClosed()
                    .subscribe(() => this._removeMessageFromShownList('warnings', message));
                }
                tmpShow.push(message);
              });
              (order.messages?.errors || /* istanbul ignore next */ []).forEach((message) => {
                /* istanbul ignore else */
                if (!this._messagesShown.includes(message)) {
                  this._toaster
                    .error(message)
                    .afterClosed()
                    .subscribe(() => this._removeMessageFromShownList('errors', message));
                }
                tmpShow.push(message);
              });
              this._messagesShown = [...tmpShow];
              tmpShow = [];
            })
          );
        });
      });
    }
  }

  update$(): Observable<boolean> {
    return this.rootState$.pipe(
      take(1),
      switchMap((data) => {
        this._store.dispatch(
          OrderformActions.updateOrderformRequest({
            data: data.order.order,
          })
        );
        return this._store.select(getOrderformPending).pipe(
          filter((pending) => !pending),
          take(1)
        );
      })
    );
  }

  private _updateStore(data: InterfacePost) {
    this._store.dispatch(OrderformActions.setToOrderStore({ update: data }));
  }

  /***************************** Set data to store *************************/

  /**
   * Set data to store
   * @param data
   */
  setOrderStore(data: Partial<InterfacePost>) {
    this._updateStore(data);
  }

  updateOrderformGlobalSettings(data: Partial<InterfacePost>) {
    this._store.dispatch(OrderformActions.updateToOrderformGlobalSettings({ update: data }));
  }

  /******************************** Requests ***********************/

  /**
   * Submit all data from store
   */
  validate$(): Observable<boolean> {
    return this.rootState$.pipe(
      take(1),
      switchMap((data) => {
        this._store.dispatch(
          OrderformActions.validateOrderformRequest({
            data: data.order.order,
          })
        );
        return this._store.select(getOrderformPending).pipe(
          filter((pending) => !pending),
          take(1)
        );
      })
    );
  }

  beforeSubmitOrderForm(): Observable<boolean> {
    this._store.dispatch(OrderformActions.beforeSubmitOrderformHook());
    return from(this.beforeSubmit.emit()).pipe(
      take(1),
      map((resultsBeforeSubmit) => {
        if (!resultsBeforeSubmit.reduce((valid, result) => valid && result.valid, true)) {
          this._store.dispatch(OrderformActions.beforeSubmitOrderformFailed());
          return false;
        }
        this._store.dispatch(OrderformActions.beforeSubmitOrderformSuccess());
        return true;
      })
    );
  }

  /**
   * Submit all data from store
   */
  submitOrderForm(): Observable<boolean> {
    this.submitted$.next(true);
    return this._store.select(getOrderformState).pipe(
      take(1),
      switchMap((data) => {
        if (!navigator.onLine) {
          this._toaster.error(
            $localize`:@@payment_method.error.interrupted:It looks like your transaction was interrupted and we weren't able to complete the payment. Please try again.`
          );

          return of(false);
        }

        // submit
        this._store.dispatch(
          OrderformActions.sendOrderFormRequest({
            data: {
              ...data.order.order,
              //Needed for the new process API
              orderform_id: data.order.settings.config.orderform_id, //Needed for the new process API
              main_product_id: data.order.settings.config.product_id,
              confirm_repurchase: data.order.order.confirm_repurchase,
              shipping_total: data.order.order_summary.shipping_total,
              shipping_total_undiscounted: data.order.order_summary.shipping_total_undiscounted,
              order_total_undiscounted: data.order.order_summary.order_total_undiscounted,
              order_total: data.order.order_summary.order_total,
              first_payment_at: data.order.order_summary.payment_plan?.first_payment_at,
              purchase_cause: data.order.purchase_cause,
              siteowner_id: data.order.settings.config.siteowner_id,
              language: data.order.settings.global.language, //needed for products and addons
              affiliate_name: data.order.settings.affiliate.name,
            },
          })
        );
        return this.requestPending$.pipe(
          take(1),
          map(() => true)
        );
      })
    );
  }

  checkDoubleOrder(email: string): void {
    this._store.dispatch(OrderformActions.checkDoubleOrderRequest({ email }));
  }

  private _removeMessageFromShownList(type: OrderformMessageType, message: string) {
    const messageIndex = this._messagesShown.findIndex((item) => item === message);
    if (messageIndex < 0) {
      return;
    }
    this._messagesShown.splice(messageIndex, 1);

    this._store.dispatch(OrderformActions.afterMessageShown({ messageType: type, message }));
  }

  removeValidationError(postname: string) {
    this._store.dispatch(OrderformActions.removeValidationError({ field: postname }));
  }

  /**
   * Load saved progress from localStorage
   */
  loadProgressFromLocalStorage(): void {
    if (!this._localStorageService.isLocalStorageAvailable()) {
      return;
    }

    const savedProgress = this._localStorageService.loadProgress();
    if (!savedProgress) {
      return;
    }

    // Dispatch action to load saved data into the store
    this._store.dispatch(
      OrderformActions.loadProgressFromLocalStorage({
        data: savedProgress,
      })
    );
  }

  /**
   * Clear saved progress from localStorage
   */
  clearProgressFromLocalStorage(): void {
    this._store.dispatch(OrderformActions.clearProgressFromLocalStorage());
  }
}
